import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { ReactNode } from 'react';
import {
    PosMicrosIntegrationAccountingCategory,
    PosMicrosIntegrationAccountingCategoryListItem,
    PosMicrosIntegrationMicrosOutlet,
    PosMicrosIntegrationTransmissionProtocol,
} from '@mews-commander/data-access';
import {} from '@mews-commander/form';
import { UNSAVED_CHANGES_DIALOG_NAME, UnsavedChangesDialog, useDialogs } from '@mews-commander/ui';
import { createZodFormResolver, getDefaults, lt, Form } from '@mews/framework';
import { FloatingPanel, FloatingPanelProps } from '@mews/b2b-ui';
import {
    PosMicrosConfigurationState,
    usePosMicrosConfigurationContext,
} from '../../contexts/posMicrosConfigurationContext';
import { ADD_MICROS_OUTLET_DIALOG_NAME, EDIT_MICROS_OUTLET_DIALOG_NAME } from '../constant';
import { accountingCategorySchema, microsOutletFormSchema, MicrosOutletFormSchema } from './formProps';
import { MicrosOutletForm, MicrosOutletFormProps } from './MicrosOutletForm';

interface FormDialogProps extends MicrosOutletFormProps, Pick<FloatingPanelProps, 'loading' | 'title'> {
    dialogName: string;
    dialogButtonName: ReactNode;
    submit: () => void;
    shouldConfirm?: boolean;
}

const FormDialog = ({
    dialogName,
    title,
    dialogButtonName,
    loading,
    submit,
    shouldConfirm,
    isDetailedBillingEnabled,
    isMultiDepartmentEnabled,
}: FormDialogProps) => {
    const { closeDialog, openDialog } = useDialogs();

    const handleClose = () => {
        if (shouldConfirm) {
            openDialog(
                <UnsavedChangesDialog
                    onCancel={() => closeDialog(UNSAVED_CHANGES_DIALOG_NAME)}
                    onDiscardChanges={() => closeDialog(dialogName)}
                />,
                UNSAVED_CHANGES_DIALOG_NAME
            );
        } else {
            closeDialog(dialogName);
        }
    };

    return (
        <FloatingPanel
            active
            size="large"
            actions={[{ variant: 'secondary', children: dialogButtonName, onClick: submit, type: 'submit' }]}
            loading={loading}
            onClose={handleClose}
            title={title}
        >
            <MicrosOutletForm
                isDetailedBillingEnabled={isDetailedBillingEnabled}
                isMultiDepartmentEnabled={isMultiDepartmentEnabled}
            />
        </FloatingPanel>
    );
};

interface MicrosOutletDialogProps
    extends Pick<FormDialogProps, 'isDetailedBillingEnabled' | 'isMultiDepartmentEnabled'> {
    microsOutlet?: PosMicrosIntegrationMicrosOutlet;
    isEditForm?: boolean;
    rowIndex?: number;
    protocol: PosMicrosIntegrationTransmissionProtocol;
}

export const MicrosOutletFormDialog = ({
    isDetailedBillingEnabled,
    isMultiDepartmentEnabled,
    isEditForm,
    microsOutlet,
    rowIndex,
    protocol,
}: MicrosOutletDialogProps) => {
    const { setPosMicrosConfigurationState } = usePosMicrosConfigurationContext();
    const { closeDialog } = useDialogs();
    const dialogName = isEditForm ? EDIT_MICROS_OUTLET_DIALOG_NAME : ADD_MICROS_OUTLET_DIALOG_NAME;
    const dialogTitle = isEditForm ? lt('EditMicrosOutlet') : lt('AddMicrosOutlet');
    const dialogButtonName = isEditForm ? lt('Update') : lt('Add');

    const resolver = createZodFormResolver<MicrosOutletFormSchema>(microsOutletFormSchema);
    const microsOutletSchemaDefaultValues: PosMicrosIntegrationMicrosOutlet = getDefaults(microsOutletFormSchema);
    const accountingCategoryDefaultValues: PosMicrosIntegrationAccountingCategory =
        getDefaults(accountingCategorySchema);

    if (protocol === PosMicrosIntegrationTransmissionProtocol.AllianceHosp) {
        microsOutletSchemaDefaultValues.netSalesAccountingCategories = new Array(8).fill(
            accountingCategoryDefaultValues
        );
    } else {
        microsOutletSchemaDefaultValues.netSalesAccountingCategories = new Array(4).fill(
            accountingCategoryDefaultValues
        );
    }

    const microsOutletValue: PosMicrosIntegrationMicrosOutlet = {
        ...microsOutlet,
        netSalesAccountingCategories: microsOutletSchemaDefaultValues.netSalesAccountingCategories.map(
            (item, index) => {
                const accountingCategory: PosMicrosIntegrationAccountingCategoryListItem = { ...item };
                accountingCategory.categoryId = microsOutlet?.netSalesAccountingCategories?.[index]?.categoryId;
                accountingCategory.taxCodes = microsOutlet?.netSalesAccountingCategories?.[index]?.taxCodes;
                return accountingCategory;
            }
        ),
    };

    const handleAddMicrosOutletValue = (
        state: PosMicrosConfigurationState,
        microsOutletFormValue: PosMicrosIntegrationMicrosOutlet
    ): PosMicrosConfigurationState => {
        if (!state.microsOutlets && array.isEmpty(state.microsOutlets)) {
            return { ...state, microsOutlets: [microsOutletFormValue] };
        }
        const microsOutlets = pipe(state.microsOutlets, array.append(microsOutletFormValue));
        return { ...state, microsOutlets };
    };

    const handleEditMicrosOutletValue = (
        state: PosMicrosConfigurationState,
        microsOutletFormValue: PosMicrosIntegrationMicrosOutlet
    ): PosMicrosConfigurationState => {
        if (!state.microsOutlets && array.isEmpty(state.microsOutlets)) {
            return { ...state, microsOutlets: [microsOutletFormValue] };
        }
        const microsOutlets = state.microsOutlets.map((item, index) => {
            if (rowIndex === index) {
                return microsOutletFormValue;
            }
            return item;
        });
        return { ...state, microsOutlets };
    };

    const handleSubmit = (formData: MicrosOutletFormSchema) => {
        setPosMicrosConfigurationState((prevState) =>
            isEditForm
                ? handleEditMicrosOutletValue(prevState, formData)
                : handleAddMicrosOutletValue(prevState, formData)
        );
        closeDialog(dialogName);
    };

    return (
        <Form
            onSubmit={handleSubmit}
            resolver={resolver}
            defaultValues={microsOutletSchemaDefaultValues}
            values={microsOutletValue}
        >
            {({ submit, hasDirtyFields }) => (
                <FormDialog
                    dialogName={dialogName}
                    dialogButtonName={dialogButtonName}
                    title={dialogTitle}
                    submit={submit}
                    shouldConfirm={hasDirtyFields}
                    isDetailedBillingEnabled={isDetailedBillingEnabled}
                    isMultiDepartmentEnabled={isMultiDepartmentEnabled}
                />
            )}
        </Form>
    );
};
